<template>
  <div class="container">
    <label :for="`FileDragDrop_${id}`" class="hitbox">
      <div
        class="drop-area"
        :class="{ disabled: disabled }"
        @drop.prevent="dropFiles"
        @dragenter.prevent.self="dragenterHandler"
        @dragleave.prevent.self="dragleaveHandler"
        ref="dropArea"
      >
        <span class="drop-area-icon-container">
          <FontAwesomeIcon
            class="drop-area-icon"
            :icon="['far', 'cloud-download']"
          />
        </span>
        <p class="drop-area-cta">
          {{ dropAreaCTA }}
        </p>
        <span class="drop-area-info">{{ dropAreaInfo }}</span>
      </div>
      <input
        :id="`FileDragDrop_${id}`"
        type="file"
        :multiple="acceptMultiple"
        :accept="accept"
        ref="fileInput"
        v-show="false"
        @change="onChange"
        :disabled="disabled"
      />
    </label>
    <div class="global-error-container" v-if="globalErrorMsg">
      {{ globalErrorMsg }}
    </div>
    <div v-for="(file, index) in files" :key="index">
      <div
        class="file-preview-container"
        :class="{
          'file-preview-container--error':
            file.error || !!validationErrors[index],
          disabled: disabled,
        }"
      >
        <div class="file-preview">
          <div class="file-preview-info">
            <span class="file-preview-icon-container">
              <FontAwesomeIcon
                class="file-preview-icon"
                :icon="['far', 'file']"
              />
            </span>
            <span class="file-preview-filename">{{
              file.originalData.name
            }}</span>
          </div>
          <span class="file-preview-close">
            <LoaderCircular
              v-if="file.isLoading"
              :height="24"
              class="button__spinner"
            />
            <FontAwesomeIcon
              v-else
              class="file-preview-close-icon"
              :icon="['far', 'times']"
              @click="deleteFile(index)"
            />
          </span>
        </div>
      </div>
      <ValidationMessage
        :displayInvalid="file.error || !!validationErrors[index]"
        :validation-message="
          validationErrorMessages[index] ||
          file.errorMessage ||
          'Something went wrong. Please try again.'
        "
      />
    </div>
    <div class="actions" v-if="showActionBtns && !disabled && files.length">
      <Button @click.native="clear" buttonStyle="secondary">
        <span>{{ clearBtn }}</span>
      </Button>
      <Button @click.native="uploadFiles" :disabled="!allFilesValid">
        <span>{{ uploadBtn }}</span>
      </Button>
    </div>
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCloudDownload,
  faFile,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Button from "../Button.vue";
import LoaderCircular from "../LoaderCircular.vue";
import InputBase from "./mixins/InputBase";

library.add(faCloudDownload, faFile, faTimes);

let id = 0;

export default {
  name: "FileDragDrop",
  mixins: [InputBase],
  components: {
    FontAwesomeIcon,
    Button,
    LoaderCircular,
  },
  props: {
    // Accepted file types. Pass "image" for all image types, or a comma-separated list of file extensions (e.g. ".csv, .xlsx, .xls")
    type: {
      type: String,
      required: true,
    },
    // Whether or not the input accepts multiple files.
    acceptMultiple: {
      type: Boolean,
      default: true,
    },
    // Disabling the input.
    disabled: {
      type: Boolean,
      default: false,
    },
    // Info text that informs on what file types are accepted.
    dropAreaInfo: {
      type: String,
    },
    // Controls whether the clear and upload buttons should be shown with the file previews
    showActionBtns: {
      type: Boolean,
      default: true,
    },
    // Label for the clear button
    clearBtn: {
      type: String,
      default: "Clear",
    },
    // Label for the upload button
    uploadBtn: {
      type: String,
      default: "Upload files",
    },
    // Handler that controls the loading & validation state for each file on upload (via either click or drag / drop)
    // This handler sets the isLoading, error and errorMessage props to each file if needed, so that these states can be properly reflected in the component.
    onUploadHandler: {
      type: Function,
      required: false,
    },
    // Message shown inside the drop area, prompting the user to click or drop files there
    dropAreaCTA: {
      type: String,
      default: "Click to upload or drag & drop file(s)",
    },
    // Error message for invalid file format
    fileFormatErrorMsg: {
      type: String,
      default: "Invalid file format, please provide valid files.",
    },
    // Error message for invalid file format
    fileAmountErrorMsg: {
      type: String,
      default: "Only one file can be accepted. Please provide one file only.",
    },
    // Toggle the validation state styling.
    validationErrors: {
      type: Array,
      default() {
        return [];
      },
    },
    // Error message that gets shown when validationError is true.
    validationErrorMessages: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      files: [],
      events: ["dragenter", "dragover", "dragleave", "drop"],
      globalErrorMsg: null,
    };
  },
  computed: {
    accept() {
      switch (this.type) {
        case "image":
          return "image/*";
        default:
          return this.type;
      }
    },
    acceptedTypes() {
      return this.type.replace(/\s+/g, "").split(",");
    },
    allFilesValid() {
      let validity = false;
      this.files.forEach((file) => {
        if (file.error === true || file.isLoading === true) validity = false;
        else validity = true;
      });
      return validity;
    },
  },
  methods: {
    preventDefaults(e) {
      e.preventDefault();
    },
    setGlobalErrorMsg(msg) {
      this.globalErrorMsg = msg;
      setTimeout(() => {
        this.globalErrorMsg = null;
      }, 2000);
    },
    dropFiles(event) {
      this.$refs.dropArea.classList.remove("dragover", "invalid-files");
      const uploadedFiles = Array.from(event.dataTransfer.files);
      if (!this.acceptMultiple && uploadedFiles.length > 1) {
        this.setGlobalErrorMsg(this.fileAmountErrorMsg);
        return;
      }
      if (this.invalidFiles(uploadedFiles)) {
        this.setGlobalErrorMsg(this.fileFormatErrorMsg);
        return;
      }
      uploadedFiles.forEach((file) => {
        this.validateAndSetFile(file);
      });
    },
    onChange(event) {
      if (!event.target.files || !event.target.files.length) return;
      const uploadedFiles = Array.from(event.target.files);
      uploadedFiles.forEach((file) => {
        this.validateAndSetFile(file);
      });
    },
    async validateAndSetFile(file) {
      const fileObject = {
        originalData: file,
        isLoading: true,
        error: false,
        errorMessage: "",
      };
      this.acceptMultiple
        ? this.files.push(fileObject)
        : (this.files = [fileObject]);
      if (this.onUploadHandler) {
        const response = await this.onUploadHandler(fileObject);
        if (response.error) {
          fileObject.error = true;
        }
        this.acceptMultiple
          ? this.inputValue.push(response.originalData)
          : (this.inputValue = response.originalData);
      } else {
        this.acceptMultiple
          ? this.inputValue.push(file)
          : (this.inputValue = file);
      }
      fileObject.isLoading = false;
    },
    deleteFile(index) {
      this.files.splice(index, 1);
      if (this.acceptMultiple) {
        this.inputValue.splice(index, 1);
      } else {
        this.inputValue = null;
        this.$refs.fileInput.value = null;
      }
      this.$emit("deleteFile", index);
    },
    clear() {
      this.files = [];
      this.acceptMultiple ? (this.inputValue = []) : (this.inputValue = null);
      this.$refs.fileInput.value = null;
      this.$emit("clear");
    },
    uploadFiles() {
      this.$emit("confirmUpload", this.inputValue);
      this.inputValue = null;
      this.files = [];
    },
    dragenterHandler() {
      this.$refs.dropArea.classList.add("dragover");
    },
    dragleaveHandler() {
      this.$refs.dropArea.classList.remove("dragover", "invalid-files");
    },
    invalidFiles(files) {
      let invalidFileType = false;
      files.forEach((file) => {
        const fileExtension = file.name
          .toLowerCase()
          .substring(file.name.lastIndexOf("."));
        if (this.type === "image") {
          if (![".png", ".jpg", ".jpeg", ".svg"].includes(fileExtension)) {
            invalidFileType = true;
          }
        } else if (!this.acceptedTypes.includes(fileExtension)) {
          invalidFileType = true;
        }
      });
      return invalidFileType;
    },
  },
  beforeCreate() {
    this.id = id.toString();
    id += 1;
  },
  mounted() {
    this.events.forEach((e) => {
      document.addEventListener(e, this.preventDefaults);
    });
  },
  unmounted() {
    this.events.forEach((e) => {
      document.removeEventListener(e, this.preventDefaults);
    });
  },
};
</script>

<style lang="scss" scoped>
@import "../../../style_variables/style_variables";
$blue_opacity_20: #438bf820;
$red_opacity_20: #c2424220;
$open_sans: "Open Sans", sans-serif;

.container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: $open_sans;
}

.hitbox {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.drop-area {
  background-color: $white;
  text-align: center;
  border: 1px solid $grey_mischka;
  border-radius: 4px;
  padding: 30px;
  width: 100%;
  transition: background-color ease 0.2s;
  cursor: pointer;
  &.invalid-files {
    cursor: no-drop;
  }
  &:hover,
  &.dragover {
    background-color: $grey_bg;
  }
  &-icon-container {
    font-size: 20px;
    color: $blue;
    background-color: $blue_opacity_20;
    border-radius: 500px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
  }
  &-cta {
    margin: 20px 0 5px;
    font-size: 16px;
  }
  &-info {
    color: $grey_bombay;
    font-size: 14px;
  }
  &.disabled {
    background-color: $grey_bg;
    color: $grey_mischka;
    cursor: default;
    .drop-area-info {
      color: $grey_mischka;
    }
    .drop-area-icon-container {
      background-color: $grey_alabaster;
      color: $grey_mischka;
    }
  }
}

.global-error-container {
  font-size: 14px;
  padding: 8px 12px;
  border: 1px solid $red;
  border-radius: 4px;
  color: $red;
  background-color: $red_opacity_20;
}

.file-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  &-container {
    background-color: $white;
    border: 1px solid $grey_mischka;
    border-radius: 4px;
    padding: 20px;
    width: 100%;
    transition: border-color ease 0.2s;
    &:hover {
      border-color: $blue;
    }
    &--error {
      border-color: $red;
      &:hover {
        border-color: $red;
      }
      .file-preview-icon-container {
        background-color: $red_opacity_20;
        color: $red;
      }
    }
    &.disabled {
      background-color: $grey_bg;
      color: $grey_mischka;
      border-color: $grey_mischka;
      .file-preview-icon-container {
        background-color: $grey_alabaster;
        color: $grey_mischka;
      }
      .file-preview-close {
        color: transparent;
        pointer-events: none;
      }
    }
  }
  &-info {
    display: flex;
    align-items: center;
  }
  &-icon-container {
    color: $blue;
    background-color: $blue_opacity_20;
    border-radius: 500px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
  }
  &-filename {
    font-size: 14px;
    margin-left: 12px;
  }
  &-close {
    &-icon {
      font-size: 20px;
      color: $grey_mischka;
      cursor: pointer;
      transition: color ease 0.2s;
      &:hover {
        color: $grey_bombay;
      }
    }
  }
}

.actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 18px;
}
</style>
